import React, { useState, useEffect } from "react";
import {
  Drawer,
  Card,
  Row,
  Col,
  Button,
  Progress,
  Modal,
  Table,
  message,
  Spin,
} from "antd";
import { EyeOutlined, FilePdfOutlined, MailOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/es/table";
import { generateDatewiseBreakdownPDF } from "../../../utils/generateDatewiseBreakdownPDF";
import ResourceSummaryReport from "./ResourceSummaryReport";
import ToolsAndMachineryReport from "./ToolsAndMachineryReport";
import MaterialSummaryReport from "./MaterialSummaryReport";
import DayProgressReport from "./DayProgressReport";
import reportApis from "../../../api/reports";
import dayjs from "dayjs";
import { formatCurrency } from "../../../utils/Utils";
import { useWbsName } from "../../ProjectScheduleManagement/shared/useWbsCodes";

interface DatewiseBreakdownDrawerProps {
  visible: boolean;
  selectedDate: any;
  onClose: () => void;
  projectId: number;
}

interface ProgressData {
  projectId: number;
  projectCompletionPercentage: number;
  budget: number;
  spend: number;
  budgetComsumption: number;
  openIssues: number;
}

const DatewiseBreakdownDrawer: React.FC<DatewiseBreakdownDrawerProps> = ({
  visible,
  selectedDate,
  onClose,
  projectId,
}) => {
  const wbsName = useWbsName();
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [budgetModalVisible, setBudgetModalVisible] = useState(false);
  const [openIssuesModalVisible, setOpenIssuesModalVisible] = useState(false);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);

  // Completion Details state
  const [completionDetailsData, setCompletionDetailsData] = useState<any[]>([]);
  const [completionDetailsLoading, setCompletionDetailsLoading] =
    useState(false);
  const [completionDetailsPagination, setCompletionDetailsPagination] =
    useState({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: false,
    });

  // Budget Consumption state
  const [budgetConsumptionData, setBudgetConsumptionData] = useState<any[]>([]);
  const [budgetConsumptionLoading, setBudgetConsumptionLoading] =
    useState(false);
  const [budgetConsumptionPagination, setBudgetConsumptionPagination] =
    useState({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: false,
    });

  // Open Issues state
  const [openIssuesData, setOpenIssuesData] = useState<any[]>([]);
  const [openIssuesLoading, setOpenIssuesLoading] = useState(false);
  const [openIssuesPagination, setOpenIssuesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: false,
  });

  const completionDetailsColumns: ColumnType<any>[] = [
    {
      title: "Activity",
      dataIndex: "activityName",
      key: "activityName",
      render: (text: any) => wbsName(text),
    },
    { title: "Task", dataIndex: "taskName", key: "taskName" },
    { title: "QTY", dataIndex: "workQuantity", key: "workQuantity" },
    { title: "UOM", dataIndex: "workUom", key: "workUom" },
    {
      title: "Executed Till Date",
      dataIndex: "executedQty",
      key: "executedQty",
    },
    {
      title: "Progress %",
      dataIndex: "taskCompletionPercentage",
      key: "taskCompletionPercentage",
      render: (text) => (text ? `${Number(text).toFixed(2)}%` : "0%"),
    },
  ];

  const budgetConsumptionColumns: ColumnType<any>[] = [
    {
      title: "Category",
      dataIndex: "budgetCategoryName",
      key: "budgetCategoryName",
    },
    {
      title: "Budget",
      dataIndex: "budget",
      key: "budget",
      render: (text) => (text ? formatCurrency(text, "INR") : "N/A"),
    },
    {
      title: "Spend",
      dataIndex: "budgetSpend",
      key: "budgetSpend",
      render: (text) => (text ? formatCurrency(text, "INR") : "N/A"),
    },
    {
      title: "%",
      dataIndex: "budgetComsumption",
      key: "budgetComsumption",
      render: (text) => (text ? `${Number(text).toFixed(2)}%` : "0%"),
    },
  ];

  const openIssuesColumns: ColumnType<any>[] = [
    { title: "Task Name", dataIndex: "taskName", key: "taskName" },
    { title: "Issue Type", dataIndex: "issueType", key: "issueType" },
    { title: "Issue Name", dataIndex: "issueName", key: "issueName" },
    {
      title: "Issue Priority",
      dataIndex: "issuePriority",
      key: "issuePriority",
    },
    { title: "Issue Status", dataIndex: "issueStatus", key: "issueStatus" },
    {
      title: "Issue Raised On",
      dataIndex: "issueRaisedOn",
      key: "issueRaisedOn",
      render: (text) => (text ? dayjs(text).format("DD-MMM-YYYY") : "N/A"),
    },
    { title: "Assigned To", dataIndex: "assignedto", key: "assignedto" },
  ];

  const fetchCompletionDetails = async (params: any) => {
    setCompletionDetailsLoading(true);
    try {
      const { current, pageSize, direction, pageDiff, paginationId } = params;

      const apiResponse = await reportApis.fetchPMSReports({
        projectIds: projectId,
        page: current,
        pageSize: pageSize,
        reportName: "dailyProgressProjectCompletionListReport",
        endDate:
          selectedDate?.actualStartDate &&
          dayjs(selectedDate.actualStartDate).format("YYYY-MM-DD"),
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count } = data;
        setCompletionDetailsPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list) setCompletionDetailsData(list);
      }
    } catch (error) {
      console.error("Error loading completion details:", error);
      message.error("Failed to load completion details");
    } finally {
      setCompletionDetailsLoading(false);
    }
  };

  const fetchBudgetConsumption = async (params: any) => {
    setBudgetConsumptionLoading(true);
    try {
      const { current, pageSize, direction, pageDiff, paginationId } = params;

      const apiResponse = await reportApis.fetchPMSReports({
        projectIds: projectId,
        page: current,
        pageSize: pageSize,
        reportName: "dailyProgressBudgetConsumptionList",
        endDate:
          selectedDate?.actualStartDate &&
          dayjs(selectedDate.actualStartDate).format("YYYY-MM-DD"),
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count } = data;
        setBudgetConsumptionPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list) setBudgetConsumptionData(list);
      }
    } catch (error) {
      console.error("Error loading budget consumption:", error);
      message.error("Failed to load budget consumption");
    } finally {
      setBudgetConsumptionLoading(false);
    }
  };

  const fetchOpenIssues = async (params: any) => {
    setOpenIssuesLoading(true);
    try {
      const { current, pageSize, direction, pageDiff, paginationId } = params;

      const apiResponse = await reportApis.fetchPMSReports({
        projectIds: projectId,
        page: current,
        pageSize: pageSize,
        reportName: "dailyProgressOpenSnagsList",
        endDate:
          selectedDate?.actualStartDate &&
          dayjs(selectedDate.actualStartDate).format("YYYY-MM-DD"),
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count } = data;
        setOpenIssuesPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list) setOpenIssuesData(list);
      }
    } catch (error) {
      console.error("Error loading open issues:", error);
      message.error("Failed to load open issues");
    } finally {
      setOpenIssuesLoading(false);
    }
  };

  const handleCompletionTableChange = async (newPagination: any) => {
    const apiParams = {
      current: newPagination.current?.toString(),
      pageSize: completionDetailsPagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (newPagination.current !== completionDetailsPagination.current) {
      apiParams.direction =
        newPagination.current > completionDetailsPagination.current ? "F" : "B";
      apiParams.paginationId =
        apiParams.direction === "F"
          ? completionDetailsData[completionDetailsData.length - 1]
              ?.paginationId
          : completionDetailsData[0]?.paginationId;
      apiParams.pageDiff =
        Math.abs(newPagination.current - completionDetailsPagination.current) -
        1;
    }

    setCompletionDetailsPagination({
      ...completionDetailsPagination,
      current: apiParams.current,
    });

    await fetchCompletionDetails(apiParams);
  };

  const handleBudgetTableChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      current: newPagination.current?.toString(),
      pageSize: budgetConsumptionPagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (newPagination.current !== budgetConsumptionPagination.current) {
      apiParams.direction =
        newPagination.current > budgetConsumptionPagination.current ? "F" : "B";
      apiParams.paginationId =
        apiParams.direction === "F"
          ? budgetConsumptionData[budgetConsumptionData.length - 1]
              ?.paginationId
          : budgetConsumptionData[0]?.paginationId;
      apiParams.pageDiff =
        Math.abs(newPagination.current - budgetConsumptionPagination.current) -
        1;
    }

    setBudgetConsumptionPagination({
      ...budgetConsumptionPagination,
      current: apiParams.current,
    });

    await fetchBudgetConsumption(apiParams);
  };

  const handleOpenIssuesTableChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      current: newPagination.current?.toString(),
      pageSize: openIssuesPagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (newPagination.current !== openIssuesPagination.current) {
      apiParams.direction =
        newPagination.current > openIssuesPagination.current ? "F" : "B";
      apiParams.paginationId =
        apiParams.direction === "F"
          ? openIssuesData[openIssuesData.length - 1]?.paginationId
          : openIssuesData[0]?.paginationId;
      apiParams.pageDiff =
        Math.abs(newPagination.current - openIssuesPagination.current) - 1;
    }

    setOpenIssuesPagination({
      ...openIssuesPagination,
      current: apiParams.current,
    });

    await fetchOpenIssues(apiParams);
  };

  const handleGeneratePDF = async () => {
    if (!selectedDate || !projectId) return;

    const loadingMessage = message.loading("Generating PDF...", 0);

    try {
      // Fetch all data for PDF with large pageSize to get all records
      const endDate =
        selectedDate?.actualStartDate &&
        dayjs(selectedDate.actualStartDate).format("YYYY-MM-DD");

      const [
        resourceSummaryResponse,
        toolsMachineryResponse,
        materialSummaryResponse,
        dayProgressResponse,
        completionDetailsResponse,
        budgetConsumptionResponse,
        openIssuesResponse,
      ] = await Promise.all([
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "resourceSumamryDailyProgress",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "toolsAndMachinaryDailyProgress",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "materialSumamryDailyProgress",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "dayProgressInDailyProgressReport",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "dailyProgressProjectCompletionListReport",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "dailyProgressBudgetConsumptionList",
          endDate: endDate,
        }),
        reportApis.fetchPMSReports({
          projectIds: projectId,
          page: 1,
          pageSize: 1000,
          reportName: "dailyProgressOpenSnagsList",
          endDate: endDate,
        }),
      ]);

      // Extract data from responses
      const resourceSummaryData = resourceSummaryResponse?.status
        ? resourceSummaryResponse.data?.list || []
        : [];
      const toolsMachineryData = toolsMachineryResponse?.status
        ? toolsMachineryResponse.data?.list || []
        : [];
      const materialSummaryData = materialSummaryResponse?.status
        ? materialSummaryResponse.data?.list || []
        : [];
      const dayProgressData = dayProgressResponse?.status
        ? dayProgressResponse.data?.list || []
        : [];
      const completionDetailsDataPDF = completionDetailsResponse?.status
        ? completionDetailsResponse.data?.list || []
        : [];
      const budgetConsumptionDataPDF = budgetConsumptionResponse?.status
        ? budgetConsumptionResponse.data?.list || []
        : [];
      const openIssuesDataPDF = openIssuesResponse?.status
        ? openIssuesResponse.data?.list || []
        : [];

      // Generate PDF with all integrated data
      generateDatewiseBreakdownPDF(
        {
          ...selectedDate,
          progressData, // Include progress till date data
        },
        {
          resourceSummaryData,
          toolsMachineryData,
          materialSummaryData,
          dayProgressData,
          completionDetailsData: completionDetailsDataPDF,
          budgetConsumptionData: budgetConsumptionDataPDF,
          openIssuesData: openIssuesDataPDF,
        }
      );

      loadingMessage();
      message.success("PDF generated successfully!");
    } catch (error) {
      loadingMessage();
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF");
    }
  };

  const handleSendEmail = () => {
    message.info("Email functionality will be implemented soon!");
  };

  const fetchProgressData = async () => {
    if (!selectedDate || !projectId) return;

    setProgressLoading(true);
    try {
      const apiResponse = await reportApis.fetchPMSReports({
        projectIds: projectId,
        page: 1,
        pageSize: 10,
        reportName: "projectProgressBudgetIssuesForDailyProgress",
        endDate:
          selectedDate?.actualStartDate &&
          dayjs(selectedDate?.actualStartDate).format("YYYY-MM-DD"),
      });

      const { data, status } = apiResponse || {};
      if (status && data) {
        setProgressData(data.list[0]);
      }
    } catch (error) {
      console.error("Error loading progress data:", error);
      message.error("Failed to load progress data");
    } finally {
      setProgressLoading(false);
    }
  };

  useEffect(() => {
    if (visible && selectedDate) {
      fetchProgressData();
    }
  }, [visible, selectedDate, projectId]);

  useEffect(() => {
    if (completionModalVisible && selectedDate) {
      fetchCompletionDetails({
        current: 1,
        pageSize: completionDetailsPagination.pageSize,
        direction: "P",
        pageDiff: 0,
        paginationId: null,
      });
    }
  }, [completionModalVisible, selectedDate]);

  useEffect(() => {
    if (budgetModalVisible && selectedDate) {
      fetchBudgetConsumption({
        current: 1,
        pageSize: budgetConsumptionPagination.pageSize,
        direction: "P",
        pageDiff: 0,
        paginationId: null,
      });
    }
  }, [budgetModalVisible, selectedDate]);

  useEffect(() => {
    if (openIssuesModalVisible && selectedDate) {
      fetchOpenIssues({
        current: 1,
        pageSize: openIssuesPagination.pageSize,
        direction: "P",
        pageDiff: 0,
        paginationId: null,
      });
    }
  }, [openIssuesModalVisible, selectedDate]);

  return (
    <>
      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <span>{`Date-wise Breakdown - ${selectedDate?.actualStartDate || ""}`}</span>
            <div>
              <Button
                type="default"
                icon={<MailOutlined />}
                onClick={handleSendEmail}
                style={{ marginRight: 8 }}
              >
                Send Email
              </Button>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={handleGeneratePDF}
              >
                PDF
              </Button>
            </div>
          </div>
        }
        placement="right"
        width={1200}
        onClose={onClose}
        open={visible}
      >
        <Card title="Progress Till Date" style={{ marginBottom: 16 }}>
          {progressLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={16}>
              <Col span={8}>
                <Card
                  hoverable
                  onClick={() => setCompletionModalVisible(true)}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <span
                      style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)" }}
                    >
                      Completion % <EyeOutlined style={{ marginLeft: 4 }} />
                    </span>
                  </div>
                  <Progress
                    percent={
                      progressData?.projectCompletionPercentage
                        ? Number(
                            progressData.projectCompletionPercentage.toFixed(2)
                          )
                        : 0
                    }
                    strokeColor={{
                      "0%": "#108ee9",
                      "100%": "#87d068",
                    }}
                    format={(percent) => `${percent}%`}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card
                  hoverable
                  onClick={() => setBudgetModalVisible(true)}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <span
                      style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)" }}
                    >
                      Budget Consumption %{" "}
                      <EyeOutlined style={{ marginLeft: 4 }} />
                    </span>
                  </div>
                  <Progress
                    percent={
                      progressData?.budgetComsumption
                        ? Number(progressData.budgetComsumption.toFixed(2))
                        : 0
                    }
                    strokeColor={{
                      "0%": "#108ee9",
                      "100%": "#f5222d",
                    }}
                    format={(percent) => `${percent}%`}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card
                  hoverable
                  onClick={() => setOpenIssuesModalVisible(true)}
                  style={{ cursor: "pointer", height: "99px" }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <span
                      style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)" }}
                    >
                      Open Issues Count{" "}
                      <EyeOutlined style={{ marginLeft: 4 }} />
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.85)",
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {progressData?.openIssues ?? 0}
                  </div>
                </Card>
              </Col>
            </Row>
          )}
        </Card>

        <Card title="Resource Summary" style={{ marginBottom: 16 }}>
          <ResourceSummaryReport
            selectedDate={selectedDate?.actualStartDate}
            projectId={projectId}
          />
        </Card>

        <Card title="Tools and Machinery" style={{ marginBottom: 16 }}>
          <ToolsAndMachineryReport
            selectedDate={selectedDate?.actualStartDate}
            projectId={projectId}
          />
        </Card>

        <Card title="Material Summary" style={{ marginBottom: 16 }}>
          <MaterialSummaryReport
            selectedDate={selectedDate?.actualStartDate}
            projectId={projectId}
          />
        </Card>

        <Card title="Day Progress" style={{ marginBottom: 16 }}>
          <DayProgressReport
            selectedDate={selectedDate?.actualStartDate}
            projectId={projectId}
          />
        </Card>
      </Drawer>

      <Modal
        title="Completion Percentage Details"
        open={completionModalVisible}
        onCancel={() => setCompletionModalVisible(false)}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setCompletionModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={completionDetailsColumns}
          dataSource={completionDetailsData}
          pagination={completionDetailsPagination}
          onChange={handleCompletionTableChange}
          loading={completionDetailsLoading}
          size="small"
          bordered
        />
      </Modal>

      <Modal
        title="Budget Consumption Details"
        open={budgetModalVisible}
        onCancel={() => setBudgetModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setBudgetModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={budgetConsumptionColumns}
          dataSource={budgetConsumptionData}
          pagination={budgetConsumptionPagination}
          onChange={handleBudgetTableChange}
          loading={budgetConsumptionLoading}
          size="small"
          bordered
        />
      </Modal>

      <Modal
        title="Open Issues List"
        open={openIssuesModalVisible}
        onCancel={() => setOpenIssuesModalVisible(false)}
        width={1400}
        footer={[
          <Button key="close" onClick={() => setOpenIssuesModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={openIssuesColumns}
          dataSource={openIssuesData}
          pagination={openIssuesPagination}
          onChange={handleOpenIssuesTableChange}
          loading={openIssuesLoading}
          size="small"
          bordered
        />
      </Modal>
    </>
  );
};

export default DatewiseBreakdownDrawer;
